import {
  ProjectBulkButtonComponents,
  ProjectTableBulkName,
} from "@components/common/faro-table/faro-table-types";
import { BulkExportProjects } from "@components/table/projects/bulk-actions/bulk-export-projects";
import { BaseCompanyIdProps } from "@custom-types/base-types";
import {
  BaseProjectsProps,
  ProjectArchivingState,
  SdbProject,
} from "@custom-types/project-types";
import { MAP_EXPORT_EXCLUDED_COLUMNS } from "@utils/file-data-export";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { selectedEntitiesSelector } from "@store/table/table-selector";
import { resetTableState, setTableType } from "@store/table/table-slice";
import { TableType } from "@utils/track-event/track-event-list";
import { useEffect, useMemo } from "react";

interface UseProjectsMapBulkActions {
  /** The bulk action buttons for the projects map list */
  bulkActions: ProjectBulkButtonComponents;

  /** The currently selected projects */
  selectedProjects: SdbProject[];
}

interface Props extends BaseCompanyIdProps, BaseProjectsProps {
  /** The the currently selected project archiving state */
  projectArchivingState: ProjectArchivingState;
}

/**
 * Custom hook to handle the bulk actions for the projects map list.
 * @returns The available bulk actions and the currently selected projects.
 */
export function useProjectsMapBulkActions({
  companyId,
  projects,
  projectArchivingState,
}: Props): UseProjectsMapBulkActions {
  const dispatch = useAppDispatch();
  const selectedProjects = useAppSelector(
    selectedEntitiesSelector("mapProjects")
  );
  const isAnyRowSelected = selectedProjects.length !== 0;
  const isActionAllowedMap: Record<ProjectTableBulkName, boolean> =
    useMemo(() => {
      return {
        /* eslint-disable @typescript-eslint/naming-convention -- naming given by existing action identifiers */
        export: isAnyRowSelected,
        changeProjectManagement: false,
        changeProjectStatus: false,
        removeProject: false,
        changeAccessLevel: false,
        /* eslint-enable @typescript-eslint/naming-convention */
      };
    }, [isAnyRowSelected]);

  // Set the table type to mapProjects on mount and reset the table state on unmount
  useEffect(() => {
    dispatch(setTableType(TableType.mapProjects));
    return () => {
      dispatch(resetTableState());
    };
  }, [dispatch]);

  const bulkActions: ProjectBulkButtonComponents = {
    export: isActionAllowedMap.export ? (
      <BulkExportProjects
        companyId={companyId}
        selectedProjects={isAnyRowSelected ? selectedProjects : projects}
        subjectType="workspace"
        projectStatus={projectArchivingState}
        areEntitiesSelected={isAnyRowSelected}
        columnsToExclude={MAP_EXPORT_EXCLUDED_COLUMNS}
        viewMode="map"
      />
    ) : null,
    changeProjectManagement: null,
    changeProjectStatus: null,
    removeProject: null,
    changeAccessLevel: null,
  };

  return { bulkActions, selectedProjects };
}
